import type { CSSProperties, JSX } from "react";
import { cn } from "@repo/ui/utils";
import type {
  VisualType,
  VideoPlayerConfigData,
  UrlVideoSource,
  MuxVideoSource,
} from "@/lib/types";
import { ImageWrapper, ImageWrapperProps } from "../image/image.component";
import { VideoPlayerWrapper } from "../video-player/Video-player.component";
import { MuxVideoPlayer } from "../video-player/mux-video-player.component";

export interface VisualWrapperProps {
  visual: VisualType;
  className?: string;
  style?: CSSProperties;
  visualClassName?: string;
  extraImageProps?: Partial<ImageWrapperProps>;
  imgClassName?: string;
  /**
   * Render videos as decorative backgrounds — autoplay muted on a loop,
   * fill the container, hide controls and play overlay. No-op for images.
   */
  asBackground?: boolean;
  priority?: boolean;
}

export function VisualWrapper({
  visual,
  className,
  style,
  visualClassName,
  extraImageProps,
  imgClassName,
  asBackground = false,
  priority,
}: VisualWrapperProps): JSX.Element | null {
  if (!visual || !visual.mediaSource) {
    return null;
  }

  const mediaSource = visual.mediaSource;

  const renderVisual = (): JSX.Element | null => {
    switch (mediaSource._type) {
      case "visualImageItem":
        return (
          <ImageWrapper
            className={visualClassName}
            imgClassName={imgClassName}
            visual={mediaSource.customImage}
            priority={priority}
            {...extraImageProps}
          />
        );

      case "visualVideoItem": {
        const { customVideo } = mediaSource;
        const { videoSourceType, ...rest } = customVideo || {};

        if (!videoSourceType) {
          return null;
        }

        switch (videoSourceType._type) {
          case "videoUrlLink": {
            const urlSource = videoSourceType as UrlVideoSource;
            return (
              <VideoPlayerWrapper
                asBackground={asBackground}
                priority={priority}
                className={visualClassName}
                source={urlSource}
                config={rest as VideoPlayerConfigData}
              />
            );
          }
          case "muxUpload": {
            const muxSource = videoSourceType as MuxVideoSource;
            return (
              <MuxVideoPlayer
                asBackground={asBackground}
                priority={priority}
                className={visualClassName}
                source={muxSource}
                config={rest as VideoPlayerConfigData}
              />
            );
          }
          default:
            return null;
        }
      }
      default:
        return null;
    }
  };

  return (
    <div className={cn("VisualComponentWrapper", className)} style={style}>
      {renderVisual()}
    </div>
  );
}
